import { useRef } from 'react';
import axios from 'axios';
import { renderPdfPageToImage } from './pdfRender';
import { asPicture, withDoc } from './promptPictures';
import { BATCH_SIZE, SECTIONS_PAGE_WIDTH, chunk, useRun } from './categoryRun';
import { useBrand } from './brandStore';
import { useSubmodels, type Submodel } from './listingStore';
import { ACTIVE_MODELS, NOTICE_MODELS, modelLabel } from './models';
import { reasoningTooltip, tipSection } from './ReasoningBadge';
import { submodelLabel } from './variantLabel';
import { readShopProduct } from './shopProduct';
import { type CallEntry } from './CallsBadge';
import { useLanguage } from '../../contexts/LanguageContext';

// EVERY CALL PHASE 1 MAKES, as the badges over its three reads walk them: what each would ask
// — the words from the route's own preview, the pictures rendered as the run renders them —
// and what each answered last time.
//
// The answers were brain badges on the answers themselves (a row's on the Sections table, one
// on each picture strip). They are here now, beside the question that produced them, and
// nothing they showed is dropped: every block's reasoning and every page's raw reply, every
// model that answered a block, the info read's and each listing's.

type Pages = { n: number; width?: number }[];

// Render some pages, captioned, as a call carries them.
const pagePictures = (file: Blob, pages: Pages, t: (k: string) => string) =>
  withDoc(file, async (doc) => {
    const out = [];
    for (const { n, width } of pages) {
      const image = await renderPdfPageToImage(doc, n, width);
      if (image) out.push(asPicture(image, `${t('notice.pdf.page')} ${n}`, `${t('notice.pdf.page')} ${n}`));
    }
    return out;
  }, [] as ReturnType<typeof asPicture>[]);

const range = (ps: number[]) => (ps.length > 1 ? `${ps[0]}–${ps[ps.length - 1]}` : `${ps[0]}`);

// THE MANUAL'S CALLS: the sections, one call per block, each also reading the manual's info.
export function useManualCalls(docId: string, file: Blob, numPages: number): CallEntry[] {
  const { t } = useLanguage();
  const brand = useBrand(docId);
  const { reasoning, raws, segments, categories } = useRun(docId);
  const pages = Array.from({ length: numPages }, (_, i) => i + 1);

  // The model a press would send each block to — the first active one.
  const sendTo = ACTIVE_MODELS[0]?.id ?? '';
  const answered = (id: string, n: number) =>
    !!(segments[id]?.[n]?.length || categories[id]?.[n] || raws[id]?.[n] || reasoning[id]?.[n]);
  // The category carried into a block, read off the last answer — what a run over an
  // unchanged manual would carry. The first model that answered, as categoryBands reads it.
  const lastCategory = (n: number) => {
    const m = NOTICE_MODELS.find((x) => segments[x.id]?.[n]?.length);
    const segs = m ? segments[m.id][n] : undefined;
    return segs?.[segs.length - 1]?.category ?? '';
  };

  const blocks = chunk(pages, BATCH_SIZE).map((block): CallEntry => {
    // Every model that answered this block — a retired one included, since the table still
    // shows its row. One reasoning per model (a block is one call, one thought stream, stored
    // on each of its pages), then each page's raw reply.
    const models = NOTICE_MODELS.filter((m) => block.some((n) => answered(m.id, n)));
    return {
      key: `sections-${block[0]}`,
      label: t('notice.cat.group.sections'),
      sub: `${t('notice.pdf.page')} ${range(block)}`,
      load: async () => {
        const [{ data }, urls] = await Promise.all([
          axios.post<{ prompt: string; model: string }>('/api/notice/categorize-batch', {
            preview: true, model: sendTo, pages: block,
            prevCategory: block[0] > 1 ? lastCategory(block[0] - 1) : '',
          }),
          pagePictures(file, block.map((n) => ({ n, width: SECTIONS_PAGE_WIDTH })), t),
        ]);
        return { prompt: data.prompt, model: data.model, urls };
      },
      answer: models.length ? (
        <div className="space-y-5">
          {models.map((m) => (
            <div key={m.id} className="space-y-3">
              {models.length > 1 && <div className="text-xs font-semibold text-slate-700">{modelLabel(m.id)}</div>}
              {tipSection(
                t('notice.cat.reasoningTitle'),
                block.map((n) => reasoning[m.id]?.[n]).find((r) => r?.trim()) || t('notice.cat.noReasoning'),
              )}
              {block.filter((n) => answered(m.id, n)).map((n) => (
                <div key={n}>
                  {tipSection(`${t('notice.cat.rawOutputTitle')} · ${t('notice.pdf.page')} ${n}`, raws[m.id]?.[n] || '')}
                </div>
              ))}
            </div>
          ))}
          {/* The manual's info, read by these same calls: every block's answer, in order. */}
          {block[0] === 1 && brand.raw && tipSection(t('notice.cat.group.infos'), brand.raw)}
        </div>
      ) : undefined,
    };
  });

  return blocks;
}

type ListingPreview = { model: string; instructions: string; page: string; urls: string[] };

// THE LISTINGS' CALLS: one per link, its page and its pictures read together. The preview is
// asked once per link and reused until the link moves. THE INSTRUCTIONS ONCE: they are the
// same for every link, so the first link's entry shows them over its page and the others
// show their own page alone.
export function useListingCalls(docId: string): CallEntry[] {
  const { t } = useLanguage();
  const submodels = useSubmodels(docId);
  const asked = useRef(new Map<string, Promise<ListingPreview>>());

  const preview = (s: Submodel) => {
    const key = s.url;
    let p = asked.current.get(key);
    if (!p) {
      // With the product record the read itself would send, so the preview shows the same page.
      p = readShopProduct(s.url)
        .then((product) => axios.post<ListingPreview>('/api/notice/listing', {
          url: s.url, preview: true, ...(product ? { product } : {}),
        }))
        .then((r) => r.data);
      // A failed preview is not kept: the next opening asks again.
      p.catch(() => asked.current.delete(key));
      asked.current.set(key, p);
    }
    return p;
  };

  const firstLinked = submodels.findIndex((x) => x.url);
  return submodels.flatMap((s, i): CallEntry[] => (s.url ? [{
    key: s.id,
    label: submodelLabel(s, i, t('notice.listing.label')),
    sub: t('notice.listing.callRead'),
    load: async () => {
      const data = await preview(s);
      const prompt = i === firstLinked ? `${data.instructions}\n\n${data.page}` : data.page;
      return { prompt, model: data.model, urls: data.urls };
    },
    answer: s.reasoning || s.raw ? reasoningTooltip(s.reasoning, s.raw, t) : undefined,
  }] : []));
}
